import {
	CheckCircle,
	ChevronDown,
	ChevronRight,
	Code2,
	Eye,
	EyeOff,
	GitBranch,
	GripVertical,
	HelpCircle,
	Info,
	Plus,
	Search,
	X,
	XCircle,
	Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { useNodeValidation, useNodeValidationPattern } from '../../hooks/useNodeValidation';
import { evaluateExpression, validateExpression } from '../../lib/expression-evaluator';
import type {
	AdvancedCondition,
	ConditionGroup,
	ConditionNodeConfig,
	ConditionOperator,
	SimpleCondition,
} from '../../types/nodes';
import { ProcessingNodeData } from '../../types/nodes';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import { VariablePicker } from '../VariablePicker';

interface ConditionConfigProps {
	nodeId: string;
	data: ProcessingNodeData;
	onChange: (data: ProcessingNodeData) => void;
	// Wizard state callbacks for parent component
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted: boolean;
		validationFailed: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

interface FieldInfo {
	id: string;
	label: string;
	type: 'string' | 'number' | 'boolean' | 'object' | 'array';
	description?: string;
	sampleValue?: any;
}

const defaultConfig: ConditionNodeConfig = {
	match: 'all',
	conditions: [
		{
			type: 'simple',
			field: '',
			operator: '=',
			value: '',
		},
	],
};

// Type-aware operators
const OPERATOR_GROUPS = {
	string: [
		{ value: '=', label: 'Equals', description: 'Field equals the specified value exactly' },
		{
			value: '!=',
			label: 'Not Equals',
			description: 'Field does not equal the specified value',
		},
		{ value: 'contains', label: 'Contains', description: 'Field contains the specified text' },
		{
			value: 'startsWith',
			label: 'Starts With',
			description: 'Field starts with the specified text',
		},
		{
			value: 'endsWith',
			label: 'Ends With',
			description: 'Field ends with the specified text',
		},
		{ value: 'isEmpty', label: 'Is Empty', description: 'Field is empty, null, or undefined' },
		{ value: 'isNotEmpty', label: 'Is Not Empty', description: 'Field has a value' },
	],
	number: [
		{ value: '=', label: 'Equals', description: 'Field equals the specified number' },
		{
			value: '!=',
			label: 'Not Equals',
			description: 'Field does not equal the specified number',
		},
		{
			value: '>',
			label: 'Greater Than',
			description: 'Field is greater than the specified number',
		},
		{ value: '<', label: 'Less Than', description: 'Field is less than the specified number' },
		{
			value: '>=',
			label: 'Greater or Equal',
			description: 'Field is greater than or equal to the specified number',
		},
		{
			value: '<=',
			label: 'Less or Equal',
			description: 'Field is less than or equal to the specified number',
		},
		{ value: 'isEmpty', label: 'Is Empty', description: 'Field is empty, null, or undefined' },
		{ value: 'isNotEmpty', label: 'Is Not Empty', description: 'Field has a value' },
	],
	boolean: [
		{ value: '=', label: 'Equals', description: 'Field equals the specified boolean value' },
		{
			value: '!=',
			label: 'Not Equals',
			description: 'Field does not equal the specified boolean value',
		},
		{ value: 'isTrue', label: 'Is True', description: 'Field is true' },
		{ value: 'isFalse', label: 'Is False', description: 'Field is false' },
	],
	object: [
		{ value: 'isEmpty', label: 'Is Empty', description: 'Object is empty, null, or undefined' },
		{ value: 'isNotEmpty', label: 'Has Data', description: 'Object has properties' },
		{
			value: 'hasProperty',
			label: 'Has Property',
			description: 'Object has a specific property',
		},
	],
	array: [
		{ value: 'isEmpty', label: 'Is Empty', description: 'Array is empty or undefined' },
		{ value: 'isNotEmpty', label: 'Has Items', description: 'Array has one or more items' },
		{ value: 'contains', label: 'Contains', description: 'Array contains the specified value' },
		{
			value: 'length=',
			label: 'Length Equals',
			description: 'Array length equals specified number',
		},
		{
			value: 'length>',
			label: 'Length Greater Than',
			description: 'Array length is greater than specified number',
		},
		{
			value: 'length<',
			label: 'Length Less Than',
			description: 'Array length is less than specified number',
		},
	],
};

const EXAMPLE_CONDITIONS = [
	{
		name: 'OFAC Match Check',
		config: {
			match: 'all' as const,
			conditions: [
				{
					type: 'simple' as const,
					field: 'ofac.matchCount',
					operator: '>' as const,
					value: '0',
				},
			],
		},
	},
	{
		name: 'Country & Amount',
		config: {
			match: 'all' as const,
			conditions: [
				{
					type: 'simple' as const,
					field: 'submission.country',
					operator: '=' as const,
					value: 'US',
				},
				{
					type: 'simple' as const,
					field: 'submission.amount',
					operator: '>' as const,
					value: '1000',
				},
			],
		},
	},
	{
		name: 'Complex Expression',
		config: {
			match: 'any' as const,
			conditions: [
				{
					type: 'advanced' as const,
					expression: '{{submission.age}} >= 21 && {{submission.country}} === "US"',
				},
			],
		},
	},
];

export function ConditionConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
}: ConditionConfigProps) {
	const { getFlattenedFields } = useWorkflowContext();

	// Validation pattern implementation
	const { validationAttempted, validationFailed, performValidation, handleCompleteWizard } =
		useNodeValidationPattern(nodeId, data);

	// Validation display state
	const { isValidating, lastValidationResult } = useNodeValidation();
	const [config, setConfig] = useState<ConditionNodeConfig>(() => {
		const existingConfig = data.config as Partial<ConditionNodeConfig>;
		return {
			...defaultConfig,
			...existingConfig,
			conditions: existingConfig?.conditions || defaultConfig.conditions,
		};
	});
	const [previewResult, setPreviewResult] = useState<boolean | null>(null);
	const [previewError, setPreviewError] = useState<string | null>(null);
	const [conditionResults, setConditionResults] = useState<boolean[]>([]);
	const [showFieldPreview, setShowFieldPreview] = useState(false);
	const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set([0]));

	// New state for UI improvements
	const [contextSearchTerm, setContextSearchTerm] = useState('');
	const [expandedFieldCategories, setExpandedFieldCategories] = useState<Set<string>>(new Set());
	const [previewTab, setPreviewTab] = useState<'preview' | 'summary'>('preview');
	const [showProTips, setShowProTips] = useState(false);
	const [draggedCondition, setDraggedCondition] = useState<number | null>(null);

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	// Wizard state management
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode: false, // Condition config doesn't use wizard mode
				currentStep: 1,
				totalSteps: 1,
				canProceedToNext: true,
				onPreviousStep: () => {},
				onNextStep: () => {},
				onCompleteWizard: async () => {
					await handleCompleteWizard(() => {
						// Save the current configuration
						onChange({
							...data,
							config,
						});
					});
				},
				validationAttempted,
				validationFailed,
			});
		}
	}, [
		onWizardStateChange,
		config,
		validationAttempted,
		validationFailed,
		handleCompleteWizard,
		onChange,
		data,
	]);

	// Get enhanced field information with types and sample values
	const getFieldInformation = (): FieldInfo[] => {
		const flattenedFields = getFlattenedFields(nodeId);
		return flattenedFields.map(field => ({
			id: field.id,
			label: field.label,
			type: field.type,
			description: field.description,
			sampleValue: getSampleValue(field.type, field.id),
		}));
	};

	const availableFields = getFieldInformation();

	// Group fields by source category
	const getGroupedFields = () => {
		const grouped: Record<string, FieldInfo[]> = {};
		availableFields.forEach(field => {
			const category = field.id.split('.')[0] || 'Other';
			if (!grouped[category]) {
				grouped[category] = [];
			}
			grouped[category].push(field);
		});
		return grouped;
	};

	// Filter fields based on search term
	const getFilteredFields = () => {
		if (!contextSearchTerm.trim()) return availableFields;
		const searchLower = contextSearchTerm.toLowerCase();
		return availableFields.filter(
			field =>
				field.label.toLowerCase().includes(searchLower) ||
				field.id.toLowerCase().includes(searchLower) ||
				field.type.toLowerCase().includes(searchLower)
		);
	};

	// Generate human-readable summary
	const getHumanReadableSummary = () => {
		if (config.conditions.length === 0) return 'No conditions configured';

		const conditionDescriptions = config.conditions.map((condition, index) => {
			if (condition.type === 'simple' && condition.field && condition.operator) {
				const fieldLabel =
					availableFields.find(f => f.id === condition.field)?.label || condition.field;
				const operatorLabel =
					OPERATOR_GROUPS[
						getFieldType(condition.field) as keyof typeof OPERATOR_GROUPS
					]?.find(op => op.value === condition.operator)?.label || condition.operator;
				const value = condition.value || 'empty';
				return `${fieldLabel} ${operatorLabel.toLowerCase()} ${value}`;
			} else if (condition.type === 'advanced' && condition.expression) {
				return `Advanced: ${condition.expression}`;
			}
			return `Condition ${index + 1}`;
		});

		const logic = config.match === 'all' ? 'AND' : 'OR';
		const outcome =
			config.match === 'all' ? 'ALL conditions must pass' : 'ANY condition can pass';

		return `IF ${conditionDescriptions.join(` ${logic} `)} → ${outcome}`;
	};

	function getSampleValue(type: string, fieldId: string): any {
		// Generate contextual sample values based on field ID and type
		const fieldIdLower = fieldId.toLowerCase();

		if (fieldIdLower.includes('email')) return 'user@example.com';
		if (fieldIdLower.includes('name')) return 'John Doe';
		if (fieldIdLower.includes('country')) return 'US';
		if (fieldIdLower.includes('amount') || fieldIdLower.includes('price')) return 1250.5;
		if (fieldIdLower.includes('age')) return 25;
		if (fieldIdLower.includes('count')) return 3;
		if (fieldIdLower.includes('match')) return 1;
		if (fieldIdLower.includes('approved') || fieldIdLower.includes('verified')) return true;

		switch (type) {
			case 'string':
				return 'Sample Text';
			case 'number':
				return 42;
			case 'boolean':
				return true;
			case 'object':
				return { key: 'value', nested: { prop: 'data' } };
			case 'array':
				return ['item1', 'item2', 'item3'];
			default:
				return 'Sample';
		}
	}

	// Create mock context for preview
	const mockContext = availableFields.reduce((acc, field) => {
		acc[field.id] = field.sampleValue;
		return acc;
	}, {} as Record<string, any>);

	// Enhanced evaluation with detailed feedback
	useEffect(() => {
		try {
			if (config.conditions.length === 0) {
				setPreviewResult(null);
				setPreviewError(null);
				setConditionResults([]);
				return;
			}

			const results = config.conditions.map(condition =>
				evaluateCondition(condition, mockContext)
			);
			setConditionResults(results);

			if (config.match === 'all') {
				setPreviewResult(results.every(r => r === true));
			} else {
				setPreviewResult(results.some(r => r === true));
			}
			setPreviewError(null);
		} catch (error) {
			setPreviewError(error instanceof Error ? error.message : 'Evaluation error');
			setPreviewResult(null);
			setConditionResults([]);
		}
	}, [config, mockContext]);

	function evaluateCondition(
		condition: ConditionNodeConfig['conditions'][0],
		context: Record<string, any>
	): boolean {
		if (condition.type === 'simple') {
			if (!condition.field) return false;

			const fieldValue = context[condition.field];
			const compareValue = parseValue(condition.value, getFieldType(condition.field));

			switch (condition.operator) {
				case '=':
					return fieldValue === compareValue;
				case '!=':
					return fieldValue !== compareValue;
				case '>':
					return Number(fieldValue) > Number(compareValue);
				case '<':
					return Number(fieldValue) < Number(compareValue);
				case '>=':
					return Number(fieldValue) >= Number(compareValue);
				case '<=':
					return Number(fieldValue) <= Number(compareValue);
				case 'contains':
					if (Array.isArray(fieldValue)) {
						return fieldValue.includes(compareValue);
					}
					return String(fieldValue).includes(String(compareValue));
				case 'startsWith':
					return String(fieldValue).startsWith(String(compareValue));
				case 'endsWith':
					return String(fieldValue).endsWith(String(compareValue));
				case 'isEmpty':
					return (
						!fieldValue ||
						fieldValue === '' ||
						fieldValue === null ||
						fieldValue === undefined ||
						(Array.isArray(fieldValue) && fieldValue.length === 0) ||
						(typeof fieldValue === 'object' && Object.keys(fieldValue).length === 0)
					);
				case 'isNotEmpty':
					return (
						fieldValue &&
						fieldValue !== '' &&
						fieldValue !== null &&
						fieldValue !== undefined &&
						(!Array.isArray(fieldValue) || fieldValue.length > 0) &&
						(typeof fieldValue !== 'object' || Object.keys(fieldValue).length > 0)
					);
				case 'isTrue':
					return fieldValue === true;
				case 'isFalse':
					return fieldValue === false;
				case 'hasProperty':
					return (
						fieldValue && typeof fieldValue === 'object' && compareValue in fieldValue
					);
				case 'length=':
					return Array.isArray(fieldValue) && fieldValue.length === Number(compareValue);
				case 'length>':
					return Array.isArray(fieldValue) && fieldValue.length > Number(compareValue);
				case 'length<':
					return Array.isArray(fieldValue) && fieldValue.length < Number(compareValue);
				default:
					return false;
			}
		} else if (condition.type === 'advanced' && condition.expression) {
			return Boolean(evaluateExpression(condition.expression, context));
		}

		return false;
	}

	function parseValue(value: string | undefined, fieldType: string): any {
		if (!value) return '';

		// For boolean fields, parse boolean values
		if (fieldType === 'boolean') {
			if (value.toLowerCase() === 'true') return true;
			if (value.toLowerCase() === 'false') return false;
		}

		// For number fields, try to parse as number
		if (fieldType === 'number' && !isNaN(Number(value))) {
			return Number(value);
		}

		// Try general parsing
		if (!isNaN(Number(value))) {
			return Number(value);
		}

		if (value.toLowerCase() === 'true') return true;
		if (value.toLowerCase() === 'false') return false;

		return value;
	}

	function getFieldType(fieldId: string): string {
		const field = availableFields.find(f => f.id === fieldId);
		return field?.type || 'string';
	}

	function getAvailableOperators(fieldType: string) {
		return OPERATOR_GROUPS[fieldType as keyof typeof OPERATOR_GROUPS] || OPERATOR_GROUPS.string;
	}

	function needsValueInput(operator: string): boolean {
		return !['isEmpty', 'isNotEmpty', 'isTrue', 'isFalse'].includes(operator);
	}

	function shouldUseVariablePicker(fieldType: string, operator: string): boolean {
		// Use VariablePicker for string fields and property names, but not for simple values
		return fieldType === 'string' && ['hasProperty'].includes(operator);
	}

	const handleChange = (newConfig: ConditionNodeConfig) => {
		setConfig(newConfig);
		onChange({
			...data,
			config: newConfig,
		});
	};

	const addCondition = () => {
		handleChange({
			...config,
			conditions: [
				...config.conditions,
				{
					type: 'simple',
					field: '',
					operator: '=',
					value: '',
				},
			],
		});
	};

	const removeCondition = (index: number) => {
		const newConditions = [...config.conditions];
		newConditions.splice(index, 1);
		handleChange({ ...config, conditions: newConditions });
	};

	const updateCondition = (
		index: number,
		updates: Partial<SimpleCondition | AdvancedCondition | ConditionGroup>
	) => {
		const newConditions = [...config.conditions];
		const condition = newConditions[index];

		// If field type changed, reset operator and value
		if (
			'field' in updates &&
			'field' in condition &&
			updates.field &&
			updates.field !== condition.field
		) {
			const fieldType = getFieldType(updates.field);
			const availableOps = getAvailableOperators(fieldType);
			updates.operator = availableOps[0]?.value as ConditionOperator;
			updates.value = '';
		}

		newConditions[index] = { ...condition, ...updates } as
			| SimpleCondition
			| AdvancedCondition
			| ConditionGroup;
		handleChange({ ...config, conditions: newConditions });
	};

	const loadExample = (example: (typeof EXAMPLE_CONDITIONS)[0]) => {
		handleChange(example.config);
	};

	const toggleGroup = (index: number) => {
		const newExpanded = new Set(expandedGroups);
		if (newExpanded.has(index)) {
			newExpanded.delete(index);
		} else {
			newExpanded.add(index);
		}
		setExpandedGroups(newExpanded);
	};

	// Drag and drop functionality
	const handleDragStart = (e: React.DragEvent, index: number) => {
		setDraggedCondition(index);
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/html', (e.currentTarget as HTMLElement).outerHTML);
		(e.currentTarget as HTMLElement).style.opacity = '0.5';
	};

	const handleDragEnd = (e: React.DragEvent) => {
		(e.currentTarget as HTMLElement).style.opacity = '1';
		setDraggedCondition(null);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
	};

	const handleDrop = (e: React.DragEvent, dropIndex: number) => {
		e.preventDefault();
		if (draggedCondition === null || draggedCondition === dropIndex) return;

		const newConditions = [...config.conditions];
		const draggedItem = newConditions[draggedCondition];

		// Remove dragged item
		newConditions.splice(draggedCondition, 1);

		// Insert at new position
		const insertIndex = draggedCondition < dropIndex ? dropIndex - 1 : dropIndex;
		newConditions.splice(insertIndex, 0, draggedItem);

		handleChange({ ...config, conditions: newConditions });
		setDraggedCondition(null);
	};

	// Keyboard navigation for drag handles
	const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
		if (e.key === 'ArrowUp' && index > 0) {
			e.preventDefault();
			const newConditions = [...config.conditions];
			[newConditions[index], newConditions[index - 1]] = [
				newConditions[index - 1],
				newConditions[index],
			];
			handleChange({ ...config, conditions: newConditions });
		} else if (e.key === 'ArrowDown' && index < config.conditions.length - 1) {
			e.preventDefault();
			const newConditions = [...config.conditions];
			[newConditions[index], newConditions[index + 1]] = [
				newConditions[index + 1],
				newConditions[index],
			];
			handleChange({ ...config, conditions: newConditions });
		}
	};

	const renderFieldSelector = (condition: any, index: number) => (
		<div className="space-y-2">
			<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
				Field
			</label>
			<select
				value={condition.field || ''}
				onChange={e => updateCondition(index, { field: e.target.value })}
				className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors"
			>
				<option value="">Select field...</option>
				{availableFields.map(field => (
					<option key={field.id} value={field.id}>
						{field.label} ({field.type})
					</option>
				))}
			</select>
			{condition.field && showFieldPreview && (
				<div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
					<div>
						<strong>Sample:</strong> {JSON.stringify(mockContext[condition.field])}
					</div>
					<div>
						<strong>Type:</strong> {getFieldType(condition.field)}
					</div>
				</div>
			)}
		</div>
	);

	const renderOperatorSelector = (condition: any, index: number) => {
		const fieldType = condition.field ? getFieldType(condition.field) : 'string';
		const operators = getAvailableOperators(fieldType);

		return (
			<div className="space-y-2">
				<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
					Operator
					<span className="text-gray-400 dark:text-gray-500 ml-1">({fieldType})</span>
				</label>
				<div className="relative">
					<select
						value={(condition as SimpleCondition).operator || '='}
						onChange={e =>
							updateCondition(index, {
								operator: e.target.value as ConditionOperator,
								value: needsValueInput(e.target.value)
									? (condition as SimpleCondition).value
									: undefined,
							})
						}
						className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors"
					>
						{operators.map(op => (
							<option key={op.value} value={op.value} title={op.description}>
								{op.label}
							</option>
						))}
					</select>
					{/* Operator tooltip */}
					<div className="absolute right-2 top-1/2 transform -translate-y-1/2">
						<div className="group relative">
							<HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
							<div className="absolute right-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
								{operators.find(
									op => op.value === (condition as SimpleCondition).operator
								)?.description || 'Select an operator'}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderValueInput = (condition: any, index: number) => {
		const fieldType = condition.field ? getFieldType(condition.field) : 'string';
		const operator = condition.operator || '=';

		if (!needsValueInput(operator)) {
			return (
				<div className="space-y-2">
					<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
						Value
					</label>
					<div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
						No value needed
					</div>
				</div>
			);
		}

		const useVariablePicker = shouldUseVariablePicker(fieldType, operator);
		const [isFocused, setIsFocused] = useState(false);

		return (
			<div className="space-y-2">
				<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
					Value
				</label>
				{useVariablePicker ? (
					<VariablePicker
						nodeId={nodeId}
						value={condition.value || ''}
						onChange={value => updateCondition(index, { value })}
						placeholder="Enter value or {{variable}}"
						className="text-sm"
					/>
				) : (
					<div className="relative">
						<input
							type="text"
							value={condition.value || ''}
							onChange={e => updateCondition(index, { value: e.target.value })}
							onFocus={() => setIsFocused(true)}
							onBlur={() => setIsFocused(false)}
							placeholder={getValuePlaceholder(fieldType, operator)}
							className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
						/>
						{isFocused && fieldType === 'number' && (
							<div className="absolute top-full left-0 mt-1 text-xs text-blue-600 dark:text-blue-400">
								Numbers only
							</div>
						)}
					</div>
				)}
			</div>
		);
	};

	function getValuePlaceholder(fieldType: string, operator: string): string {
		if (fieldType === 'boolean') return 'true or false';
		if (fieldType === 'number') return '123';
		if (operator === 'hasProperty') return 'propertyName';
		if (operator.startsWith('length')) return '5';
		return 'comparison value';
	}

	const renderCondition = (condition: ConditionNodeConfig['conditions'][0], index: number) => {
		const isExpanded = expandedGroups.has(index);
		const hasResult = conditionResults[index] !== undefined;
		const result = conditionResults[index];

		// Determine condition status
		const getConditionStatus = () => {
			if (hasResult) {
				return result ? 'Pass' : 'Fail';
			}
			// Check if condition is complete
			if (condition.type === 'simple') {
				if (!condition.field || !condition.operator) return 'Incomplete';
				if (needsValueInput(condition.operator) && !condition.value) return 'Incomplete';
			} else if (condition.type === 'advanced') {
				if (!condition.expression) return 'Incomplete';
			}
			return 'Ready';
		};

		const status = getConditionStatus();
		const statusColors = {
			Pass: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-200 dark:border-green-700',
			Fail: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border-red-200 dark:border-red-700',
			Incomplete:
				'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700',
			Ready: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 border-blue-200 dark:border-blue-700',
		};

		return (
			<div
				key={index}
				draggable
				onDragStart={e => handleDragStart(e, index)}
				onDragEnd={handleDragEnd}
				onDragOver={handleDragOver}
				onDrop={e => handleDrop(e, index)}
				className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 ${
					draggedCondition === index ? 'opacity-50' : ''
				}`}
			>
				<div className="p-5 space-y-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							{/* Drag Handle */}
							<div
								className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
								tabIndex={0}
								onKeyDown={e => handleKeyDown(e, index)}
								title="Drag to reorder (or use arrow keys)"
							>
								<GripVertical className="w-4 h-4 text-gray-400 dark:text-gray-500" />
							</div>

							<button
								onClick={() => toggleGroup(index)}
								className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
							>
								{isExpanded ? (
									<ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
								) : (
									<ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
								)}
							</button>

							<div className="flex items-center gap-3">
								<h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
									Condition {index + 1}
								</h4>

								{/* Status Chip */}
								<span
									className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${statusColors[status]}`}
								>
									{status === 'Pass' && <CheckCircle className="w-3 h-3" />}
									{status === 'Fail' && <XCircle className="w-3 h-3" />}
									{status === 'Incomplete' && (
										<div className="w-3 h-3 border border-current rounded-full" />
									)}
									{status === 'Ready' && (
										<div className="w-3 h-3 bg-current rounded-full" />
									)}
									{status}
								</span>
							</div>

							<button
								onClick={() =>
									updateCondition(index, {
										type: condition.type === 'simple' ? 'advanced' : 'simple',
										...(condition.type === 'simple'
											? {
													expression: condition.field
														? `{{${condition.field}}} ${condition.operator} "${condition.value}"`
														: '',
													field: undefined,
													operator: undefined,
													value: undefined,
											  }
											: {
													field: '',
													operator: '=',
													value: '',
													expression: undefined,
											  }),
									})
								}
								className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors border border-blue-200 dark:border-blue-700"
							>
								<Code2 className="w-3 h-3" />
								{condition.type === 'simple' ? 'Advanced' : 'Simple'}
							</button>
						</div>
						<button
							onClick={() => removeCondition(index)}
							className="p-2 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
						>
							<X className="w-4 h-4" />
						</button>
					</div>

					{isExpanded && (
						<>
							{condition.type === 'simple' ? (
								<div className="grid grid-cols-1 md:grid-cols-3 gap-5">
									{renderFieldSelector(condition, index)}
									{renderOperatorSelector(condition, index)}
									{renderValueInput(condition, index)}
								</div>
							) : (
								<div className="space-y-3">
									<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
										Expression
									</label>
									<VariablePicker
										nodeId={nodeId}
										value={(condition as AdvancedCondition).expression || ''}
										onChange={value =>
											updateCondition(index, { expression: value })
										}
										placeholder="Enter expression (e.g., {{submission.amount}} > 100 && {{submission.country}} === 'US')"
										className="w-full text-sm"
									/>
									{(condition as AdvancedCondition).expression &&
										(() => {
											const validation = validateExpression(
												(condition as AdvancedCondition).expression!
											);
											if (!validation.valid) {
												return (
													<div className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded">
														<XCircle className="w-3 h-3" />
														{validation.error}
													</div>
												);
											}
											return null;
										})()}
								</div>
							)}
						</>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className="flex flex-col h-full">
			{/* Validation Results */}
			{(isValidating || lastValidationResult) && (
				<div className="px-6 py-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900">
					<ValidationResultDisplay
						errors={lastValidationResult?.errors || []}
						isValidating={isValidating}
					/>
				</div>
			)}

			<div className="p-6 space-y-8 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
				{/* Header */}
				<div className="flex items-center justify-between">
					<div>
						<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
							Condition Configuration
						</h3>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
							Create intelligent branching logic based on upstream data
						</p>
					</div>
					<div className="flex items-center gap-3">
						<button
							onClick={() => setShowFieldPreview(!showFieldPreview)}
							className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors border ${
								showFieldPreview
									? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-700'
									: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
							}`}
						>
							{showFieldPreview ? (
								<Eye className="w-4 h-4" />
							) : (
								<EyeOff className="w-4 h-4" />
							)}
							Field Preview
						</button>
					</div>
				</div>

				{/* Section Divider */}
				<div className="border-t border-gray-200 dark:border-gray-700"></div>

				{/* Path Outcomes Section */}
				<div className="bg-gradient-to-r from-green-50 to-red-50 dark:from-green-900/20 dark:to-red-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
					<h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
						<GitBranch className="w-4 h-4" />
						Branching Paths
					</h4>
					<div className="grid grid-cols-2 gap-4">
						<div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
							<div className="flex items-center gap-2">
								<div className="w-3 h-3 bg-green-500 rounded-full"></div>
								<div className="w-8 h-0.5 bg-green-500"></div>
							</div>
							<div>
								<div className="text-sm font-medium text-green-800 dark:text-green-200">
									True Path
								</div>
								<div className="text-xs text-green-600 dark:text-green-300">
									When {config.match === 'all' ? 'ALL' : 'ANY'} conditions pass
								</div>
							</div>
						</div>
						<div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
							<div className="flex items-center gap-2">
								<div className="w-3 h-3 bg-red-500 rounded-full"></div>
								<div className="w-8 h-0.5 border-t-2 border-dashed border-red-500"></div>
							</div>
							<div>
								<div className="text-sm font-medium text-red-800 dark:text-red-200">
									False Path
								</div>
								<div className="text-xs text-red-600 dark:text-red-300">
									When conditions fail
								</div>
							</div>
						</div>
					</div>
					<div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300">
						💡 <strong>Tip:</strong> Connect different nodes to each colored connection
						point to create branching logic. Green paths execute when your conditions
						are met, red paths when they're not.
					</div>
				</div>

				{/* Section Divider */}
				<div className="border-t border-gray-200 dark:border-gray-700"></div>

				{/* Available Context Fields */}
				{availableFields.length > 0 && (
					<div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
						<h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-4">
							Available Context Fields
						</h4>

						{/* Search Bar */}
						<div className="mb-4">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
								<input
									type="text"
									placeholder="Search fields by name, type, or description..."
									value={contextSearchTerm}
									onChange={e => setContextSearchTerm(e.target.value)}
									className="w-full pl-10 pr-4 py-2 text-sm border border-blue-200 dark:border-blue-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
								/>
							</div>
						</div>

						{/* Grouped Fields */}
						{contextSearchTerm ? (
							// Show filtered results
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								{getFilteredFields().map(field => (
									<div
										key={field.id}
										className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-600 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
									>
										<div className="flex-1 min-w-0">
											<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
												{field.label}
											</div>
											<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
												{field.id}
											</div>
										</div>
										<div className="text-right ml-3">
											<div className="text-xs font-medium text-blue-700 dark:text-blue-400 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded">
												{field.type}
											</div>
											{showFieldPreview && (
												<div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-24 truncate">
													{JSON.stringify(field.sampleValue)}
												</div>
											)}
										</div>
									</div>
								))}
							</div>
						) : (
							// Show grouped fields
							<div className="space-y-4">
								{Object.entries(getGroupedFields()).map(([category, fields]) => (
									<div
										key={category}
										className="border border-blue-200 dark:border-blue-600 rounded-lg overflow-hidden"
									>
										<button
											onClick={() => {
												const newExpanded = new Set(
													expandedFieldCategories
												);
												if (newExpanded.has(category)) {
													newExpanded.delete(category);
												} else {
													newExpanded.add(category);
												}
												setExpandedFieldCategories(newExpanded);
											}}
											className="w-full flex items-center justify-between p-3 bg-blue-100 dark:bg-blue-900/30 text-left hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
										>
											<span className="text-sm font-medium text-blue-900 dark:text-blue-100 capitalize">
												{category} ({fields.length})
											</span>
											{expandedFieldCategories.has(category) ? (
												<ChevronDown className="w-4 h-4 text-blue-700 dark:text-blue-300" />
											) : (
												<ChevronRight className="w-4 h-4 text-blue-700 dark:text-blue-300" />
											)}
										</button>

										{expandedFieldCategories.has(category) && (
											<div className="p-3 bg-white dark:bg-gray-800 space-y-2">
												{fields.map(field => (
													<div
														key={field.id}
														className="flex items-center justify-between p-2 rounded border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
													>
														<div className="flex-1 min-w-0">
															<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
																{field.label}
															</div>
															<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
																{field.id}
															</div>
														</div>
														<div className="text-right ml-3">
															<div className="text-xs font-medium text-blue-700 dark:text-blue-400 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded">
																{field.type}
															</div>
															{showFieldPreview && (
																<div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-24 truncate">
																	{JSON.stringify(
																		field.sampleValue
																	)}
																</div>
															)}
														</div>
													</div>
												))}
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				)}

				{/* Section Divider */}
				<div className="border-t border-gray-200 dark:border-gray-700"></div>

				{/* Match Logic & Examples */}
				<div className="flex items-center justify-between p-5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Match Logic
						</label>
						<select
							value={config.match}
							onChange={e =>
								handleChange({ ...config, match: e.target.value as 'all' | 'any' })
							}
							className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors"
						>
							<option value="all">ALL conditions must pass (AND)</option>
							<option value="any">ANY condition can pass (OR)</option>
						</select>
					</div>

					<div className="text-right">
						<div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
							Condition Presets
						</div>
						<div className="flex gap-2">
							{EXAMPLE_CONDITIONS.map(example => (
								<button
									key={example.name}
									onClick={() => loadExample(example)}
									className="flex items-center gap-1.5 px-3 py-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-600 transition-colors"
								>
									<Zap className="w-3 h-3" />
									{example.name}
								</button>
							))}
						</div>
					</div>
				</div>

				{/* Section Divider */}
				<div className="border-t border-gray-200 dark:border-gray-700"></div>

				{/* Conditions */}
				<div className="space-y-6 py-2">
					<div className="flex items-center justify-between">
						<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
							Conditions
						</h4>
						<div className="flex gap-3">
							<button
								onClick={addCondition}
								className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg border-2 border-green-200 dark:border-green-600 hover:border-green-300 dark:hover:border-green-500 transition-all duration-200 hover:shadow-sm"
							>
								<Plus className="w-4 h-4" />
								Add Condition
							</button>
						</div>
					</div>

					{config.conditions.length === 0 ? (
						<div className="text-center py-16 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800">
							<XCircle className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
							<p className="text-base font-medium">No conditions configured</p>
							<p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
								Click "Add Condition" to create your first condition
							</p>
						</div>
					) : (
						<div className="space-y-4">
							{config.conditions.map((condition, index) =>
								renderCondition(condition, index)
							)}
						</div>
					)}
				</div>

				{/* Section Divider */}
				<div className="border-t border-gray-200 dark:border-gray-700"></div>

				{/* Live Preview with Tabs */}
				{config.conditions.length > 0 && (
					<div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
						{/* Tab Headers */}
						<div className="flex border-b border-gray-200 dark:border-gray-700">
							<button
								onClick={() => setPreviewTab('preview')}
								className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
									previewTab === 'preview'
										? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-b-2 border-blue-500'
										: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
								}`}
							>
								Preview
							</button>
							<button
								onClick={() => setPreviewTab('summary')}
								className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
									previewTab === 'summary'
										? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-b-2 border-blue-500'
										: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
								}`}
							>
								Summary
							</button>
						</div>

						{/* Tab Content */}
						<div className="p-5">
							{previewTab === 'preview' ? (
								/* Preview Tab */
								previewError ? (
									<div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-400">
										<XCircle className="w-5 h-5 flex-shrink-0" />
										<span>
											<strong>Error:</strong> {previewError}
										</span>
									</div>
								) : availableFields.length === 0 ? (
									<div className="text-center py-8 text-gray-500 dark:text-gray-400">
										<Info className="w-8 h-8 mx-auto mb-2" />
										<p className="text-sm">No sample data available</p>
										<p className="text-xs mt-1">
											Connect upstream nodes to see live preview
										</p>
									</div>
								) : (
									<div className="space-y-4">
										{/* Overall Result */}
										<div className="flex items-center gap-4">
											<div
												className={`flex items-center gap-3 px-5 py-3 rounded-lg border-2 font-medium ${
													previewResult === true
														? 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-500 text-green-800 dark:text-green-200'
														: previewResult === false
														? 'bg-red-100 dark:bg-red-900/40 border-red-400 dark:border-red-500 text-red-800 dark:text-red-200'
														: 'bg-gray-100 dark:bg-gray-700 border-gray-400 dark:border-gray-500 text-gray-600 dark:text-gray-300'
												}`}
											>
												{previewResult === true ? (
													<CheckCircle className="w-5 h-5" />
												) : previewResult === false ? (
													<XCircle className="w-5 h-5" />
												) : (
													<div className="w-5 h-5 border-2 border-gray-400 rounded-full animate-pulse" />
												)}
												<span className="text-base">
													{previewResult === true
														? 'Condition PASSES ✓'
														: previewResult === false
														? 'Condition FAILS ✗'
														: 'Ready to evaluate...'}
												</span>
											</div>

											<div className="text-sm text-gray-500 dark:text-gray-400 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
												Logic:{' '}
												{config.match === 'all'
													? 'ALL must pass'
													: 'ANY can pass'}
											</div>
										</div>

										{/* Individual Results */}
										{config.conditions.length > 1 &&
											conditionResults.length > 0 && (
												<div className="border-t border-gray-200 dark:border-gray-600 pt-4">
													<div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
														Individual Results:
													</div>
													<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
														{config.conditions.map((_, index) => {
															const result = conditionResults[index];
															return (
																<div
																	key={index}
																	className={`flex items-center gap-3 p-3 rounded-lg border ${
																		result
																			? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-600'
																			: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-600'
																	}`}
																>
																	{result ? (
																		<CheckCircle className="w-4 h-4" />
																	) : (
																		<XCircle className="w-4 h-4" />
																	)}
																	<span className="text-sm font-medium">
																		Condition {index + 1}:{' '}
																		{result ? 'Pass' : 'Fail'}
																	</span>
																</div>
															);
														})}
													</div>
												</div>
											)}
									</div>
								)
							) : (
								/* Summary Tab */
								<div className="space-y-4">
									<div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
										Human-readable condition logic:
									</div>
									<div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
										<div className="text-sm font-mono text-gray-900 dark:text-gray-100">
											{getHumanReadableSummary()}
										</div>
									</div>
									{previewResult !== null && (
										<div className="flex items-center gap-2 text-sm">
											<span className="text-gray-600 dark:text-gray-400">
												Result:
											</span>
											<span
												className={`font-medium ${
													previewResult
														? 'text-green-600 dark:text-green-400'
														: 'text-red-600 dark:text-red-400'
												}`}
											>
												{previewResult ? 'True Path' : 'False Path'}
											</span>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				)}

				{/* Section Divider */}
				<div className="border-t border-gray-200 dark:border-gray-700"></div>

				{/* Collapsible Help Panel */}
				<div className="border border-amber-200 dark:border-amber-700 rounded-lg overflow-hidden">
					<button
						onClick={() => setShowProTips(!showProTips)}
						className="w-full flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
					>
						<div className="flex items-center gap-2">
							<HelpCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
							<span className="text-sm font-medium text-amber-900 dark:text-amber-200">
								Need help? 💡 Pro Tips
							</span>
						</div>
						{showProTips ? (
							<ChevronDown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
						) : (
							<ChevronRight className="w-4 h-4 text-amber-600 dark:text-amber-400" />
						)}
					</button>

					{showProTips && (
						<div className="p-4 bg-white dark:bg-gray-800 border-t border-amber-200 dark:border-amber-700">
							<div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
								<div className="flex items-start gap-2">
									<span className="text-amber-600 dark:text-amber-400 mt-0.5">
										•
									</span>
									<span>
										Field types determine available operators (string, number,
										boolean, etc.)
									</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-amber-600 dark:text-amber-400 mt-0.5">
										•
									</span>
									<span>
										Use "Is Empty" to check for null, undefined, or empty values
									</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-amber-600 dark:text-amber-400 mt-0.5">
										•
									</span>
									<span>
										Array fields support length comparisons and contain checks
									</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-amber-600 dark:text-amber-400 mt-0.5">
										•
									</span>
									<span>
										Toggle to "Advanced" mode for complex expressions with
										multiple fields
									</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-amber-600 dark:text-amber-400 mt-0.5">
										•
									</span>
									<span>
										Preview shows live evaluation with sample data from upstream
										nodes
									</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-amber-600 dark:text-amber-400 mt-0.5">
										•
									</span>
									<span>
										Use drag handles (⋮⋮) or arrow keys to reorder conditions
									</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-amber-600 dark:text-amber-400 mt-0.5">
										•
									</span>
									<span>
										Switch between Preview and Summary tabs to see different
										views of your logic
									</span>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Microcopy for Confidence */}
				<div className="text-xs text-gray-500 dark:text-gray-400 text-center p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
					💡 This change only updates UI configuration. No data model or backend logic is
					modified.
				</div>
			</div>
		</div>
	);
}
